<template>
    <div class="grid flex gap-4 border rounded px-4 py-4"
         :class="{ 'grid-cols-5': conditionElement === 'partner_tag', 'grid-cols-4': conditionElement !== 'partner_tag' }"
    >

        <div>
            <div class="flex relative rounded-md">
                <span
                    v-if="typeIsPercent"
                    class="inline-flex items-center px-2.5 text-gray-500 bg-gray-50 rounded-l border border-r-0 border-gray-300 text-sm">
                    %
                </span>
                <input
                    type="text"
                    v-model="commissionValue"
                    class="block w-full rounded-r border-gray-300 transition-shadow sm:text-sm focus:ring-0 focus:border-gray-400"
                    :class="{ 'rounded-l': !typeIsPercent }"
                    @input="updateCommissionValue($event.target.value)"
                    placeholder="e.g. 10"
                />
                <div class="flex items-center text-sm text-gray-500 ml-4">if</div>
            </div>
        </div>

        <select-field
            v-model="conditionElement"
            @input="updateConditionElement($event.target.value)"
        >
            <option value="">select</option>
            <option v-for="conditionElement in conditionElements" :value="conditionElement.key">
                {{ conditionElement.label }}
            </option>
        </select-field>

        <div v-if="conditionElement === 'first_sale'" class="flex items-center text-sm text-gray-500">
            from the customer.
        </div>

        <select-field
            v-if="conditionElement !== 'first_sale'"
            v-model="conditionType"
            @input="updateConditionType($event.target.value)"
        >
            <option value="">select</option>
            <option v-for="conditionType in conditionTypes" :value="conditionType.key">
                {{ conditionType.label }}
            </option>
        </select-field>

        <div
            class="flex items-center justify-end gap-2"
            :class="{ 'col-span-2': conditionElement === 'partner_tag', '': conditionElement !== 'partner_tag' }"
        >
            <text-field
                ref="condition_value_ref"
                v-if="['first_sale', 'partner_tag'].includes(conditionElement) === false"
                type="text"
                v-model="conditionValue"
                @input="updateConditionValue($event.target.value)"
                placeholder="e.g. 50"
            >
            </text-field>
            <btn
                v-if="showConditionPeriodSettings"
                :icon-name="accordionOpen === true ? 'arrow-up-s' : 'arrow-down-s'"
                @click="() => accordionOpen = !accordionOpen"
                type="secondary"
            >
            </btn>

            <div class="w-full" v-if="conditionElement === 'partner_tag'">
                <vue3-tags-input
                    v-model:tags="partnerTags"
                    :select="true"
                    :allow-duplicates="false"
                    :select-items="availablePartnerTags"
                    placeholder="Select partner tags"
                    @on-select="handleSelectedTag"
                    @on-tags-changed="handleChangeTag"
                    :add-tag-on-blur="true"
                >
                    <template #item="{ tag, index }">
                        {{ tag.value }}
                    </template>
                    <template #no-data>
                        No partner tags available.
                    </template>
                    <template #select-item="tag">
                        {{ tag.value }}
                    </template>
                </vue3-tags-input>
            </div>

            <btn
                icon-name="trash"
                type="secondary"
                @click="remove"
            >
            </btn>
        </div>

        <div v-if="showConditionPeriodSettings" v-show="accordionOpen === true"
             class="col-span-4 grid grid-cols-4 gap-4 flex items-center border-t pt-4">
            <div class="flex relative rounded">
                <div class="flex items-center text-sm text-gray-500 mr-4">Condition value period</div>
            </div>
            <select-field
                v-model="conditionValuePeriod"
                @input="updateConditionValuePeriod($event.target.value)"
            >
                <option value="all_time">all time</option>
                <option value="previous">previous</option>
            </select-field>
            <div v-if="conditionValuePeriod === 'previous'" class="flex relative rounded gap-1 col-span-2">
                <text-field
                    type="text"
                    v-model="conditionValuePeriodTimes"
                    @input="updateConditionValuePeriodTimes($event.target.value)"
                    class="w-1/3"
                    placeholder="e.g. 1"
                >
                </text-field>
                <select-field
                    v-model="conditionValuePeriodType"
                    @input="updateConditionValuePeriodType($event.target.value)">
                    <option value="">select</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                </select-field>
            </div>
        </div>
    </div>
</template>

<script setup>

import {ref, computed, onMounted} from 'vue';
import Vue3TagsInput from "vue3-tags-input";

const props = defineProps({
    commissionValue: String,
    conditionElement: String,
    conditionType: String,
    conditionValue: {
        type: [String, Array],
        default: ''
    },
    conditionElements: Object,
    conditionTypes: Object,
    typeIsPercent: Boolean,
    conditionValuePeriod: String,
    conditionValuePeriodTimes: String,
    conditionValuePeriodType: String,
    withoutPartnerTags: {
        type: Boolean,
        defaults: false
    },
    availablePartnerTags: {
        type: Array,
        default: []
    },
});

const conditionElements = ref(props.conditionElements);
const commissionValue = ref(props.commissionValue);
const conditionElement = ref(props.conditionElement);
const conditionType = ref(props.conditionType);
const conditionValue = ref(props.conditionValue);
const conditionNumericValueBkp = ref(props.conditionValue);

const conditionValuePeriod = ref(props.conditionValuePeriod || 'all_time');
const conditionValuePeriodTimes = ref(props.conditionValuePeriodTimes);
const conditionValuePeriodType = ref(props.conditionValuePeriodType);
const accordionOpen = ref(false);

const partnerTags = ref([]);
const availablePartnerTags = ref(props.availablePartnerTags);

const emit = defineEmits([
    'update:commissionValue',
    'update:conditionElement',
    'update:conditionType',
    'update:conditionValue',
    'update:conditionValuePeriod',
    'update:conditionValuePeriodTimes',
    'update:conditionValuePeriodType',
    'remove'
]);

onMounted(() => {

    if (props.withoutPartnerTags) {

        if (conditionElement.value === 'partner_tag') {
            conditionElement.value = null;
            conditionValue.value = '';
        }

        const newObj = {...conditionElements.value};
        delete newObj['partner_tag'];
        conditionElements.value = newObj;

        conditionNumericValueBkp.value = '';
    }

    /**
     * Prep partner tags
     */
    if (conditionElement.value === 'partner_tag') {
        conditionValue.value.forEach(tag => {
            for (const [i, availableTag] of Object.entries(availablePartnerTags.value)) {
                if (availableTag.key === tag) {
                    partnerTags.value.push(availableTag);
                }
            }
        });
    }

    /**
     * Prep numeric condition value backup
     */
    if (conditionElement.value === 'partner_tag') {
        conditionNumericValueBkp.value = '';
    }
});

const updateCommissionValue = (value) => {
    emit('update:commissionValue', value);
};

const updateConditionElement = (value) => {

    if (value === 'partner_tag') {

        if (['belongs_to', 'doesnt_belong_to', 'belongs_to_any', 'doesnt_belong_to_any'].includes(conditionType.value) !== true) {
            conditionType.value = '';
            updateConditionType(conditionType.value);
        }

        conditionNumericValueBkp.value = conditionValue.value;
        conditionValue.value = partnerTags.value.map(tag => tag.key);
    } else {

        if (['belongs_to', 'doesnt_belong_to', 'belongs_to_any', 'doesnt_belong_to_any'].includes(conditionType.value) === true) {
            conditionType.value = '';
            updateConditionType(conditionType.value);
        }

        conditionValue.value = conditionNumericValueBkp.value;
    }

    updateConditionValue(conditionValue.value);

    emit('update:conditionElement', value);
};

const updateConditionType = (value) => {
    emit('update:conditionType', value);
};

const updateConditionValue = (value) => {
    emit('update:conditionValue', value);
};

const updateConditionValuePeriod = (value) => {
    emit('update:conditionValuePeriod', value);
};

const updateConditionValuePeriodTimes = (value) => {
    emit('update:conditionValuePeriodTimes', value);
};

const updateConditionValuePeriodType = (value) => {
    emit('update:conditionValuePeriodType', value);
};

const remove = () => {
    emit('remove');
};

const handleSelectedTag = (tag) => {

    const exists = partnerTags.value.some(item => item.key === tag.key);
    if (!exists) {
        partnerTags.value.push(tag);
    }

    conditionValue.value = partnerTags.value.map(tag => tag.key);

    updateConditionValue(conditionValue.value);
}

const handleChangeTag = (tags) => {
    partnerTags.value = tags;
    conditionValue.value = tags.map(tag => tag.key);

    updateConditionValue(conditionValue.value);
}

const showConditionPeriodSettings = computed(() => {
    return ['single_sale', 'first_sale', 'partner_tag'].includes(conditionElement.value) === false;
});

const conditionTypes = computed(() => {

    let types = {};
    let availableTypes = ['eq', 'gt', 'lt'];

    if (conditionElement.value === 'first_sale') {
        return {};
    }

    if (conditionElement.value === 'partner_tag') {
        availableTypes = ['belongs_to', 'doesnt_belong_to', 'belongs_to_any', 'doesnt_belong_to_any'];
    }

    availableTypes.forEach(type => {
        if (props.conditionTypes[type]) {
            types[type] = props.conditionTypes[type];
        }
    });

    return types;
});
</script>
<style>
.v3ti {
    border-radius: 4px !important;
    border-color: rgb(209, 213, 219) !important;
}
.v3ti-new-tag {
    font-size: .875rem;
    height: 29px !important;
}

</style>
